/**
 * GeometryViewer Component
 *
 * Main 3D viewer component for rendering EnergyPlus building geometry.
 */

import { useCallback, useRef, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, GizmoHelper, GizmoViewport, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import type { BuildingGeometry, ColorScheme, ViewMode, CameraPreset } from '../types';
import { SurfaceMesh } from './SurfaceMesh';
import { FenestrationMesh } from './FenestrationMesh';

interface GeometryViewerProps {
  geometry: BuildingGeometry | null;
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
  viewMode: ViewMode;
  colorScheme: ColorScheme;
  showNormals?: boolean | undefined;
  showGrid?: boolean | undefined;
  showAxes?: boolean | undefined;
  cameraPreset?: CameraPreset | undefined;
  fitAllTrigger?: number | undefined;
  zoomToSelectionTrigger?: number | undefined;
}

interface SceneContentProps {
  geometry: BuildingGeometry;
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
  viewMode: ViewMode;
  colorScheme: ColorScheme;
  showNormals?: boolean | undefined;
  showGrid?: boolean | undefined;
  showAxes?: boolean | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>;
  cameraPreset?: CameraPreset | undefined;
  fitAllTrigger?: number | undefined;
  zoomToSelectionTrigger?: number | undefined;
}

/**
 * Camera controller component for preset views.
 */
function CameraController({
  geometry,
  controlsRef,
  cameraPreset,
  fitAllTrigger,
  zoomToSelectionTrigger,
  selectedIds,
}: {
  geometry: BuildingGeometry;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>;
  cameraPreset?: CameraPreset | undefined;
  fitAllTrigger?: number | undefined;
  zoomToSelectionTrigger?: number | undefined;
  selectedIds?: string[] | undefined;
}) {
  const { camera } = useThree();

  // Calculate geometry metrics
  const { boundingBox, center } = geometry;
  const size = Math.max(
    boundingBox.max.x - boundingBox.min.x,
    boundingBox.max.y - boundingBox.min.y,
    boundingBox.max.z - boundingBox.min.z
  );
  const distance = size * 2;

  // Move camera to preset position
  const moveToPreset = useCallback((preset: CameraPreset) => {
    const position = getCameraPresetPosition(preset, center, distance);
    camera.position.copy(position);
    camera.lookAt(center.x, center.z, center.y);

    if (controlsRef.current) {
      controlsRef.current.target.set(center.x, center.z, center.y);
      controlsRef.current.update();
    }
  }, [camera, center, distance, controlsRef]);

  // Fit all - move to isometric view
  const fitToView = useCallback(() => {
    moveToPreset('isometric');
  }, [moveToPreset]);

  // Zoom to selected objects
  const zoomToSelection = useCallback(() => {
    if (!selectedIds || selectedIds.length === 0) return;

    // Find bounding box of selected surfaces
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let found = false;

    for (const zone of geometry.zones) {
      for (const surface of zone.surfaces) {
        if (selectedIds.includes(surface.id)) {
          found = true;
          for (const v of surface.vertices) {
            minX = Math.min(minX, v.x);
            minY = Math.min(minY, v.y);
            minZ = Math.min(minZ, v.z);
            maxX = Math.max(maxX, v.x);
            maxY = Math.max(maxY, v.y);
            maxZ = Math.max(maxZ, v.z);
          }
        }
        // Also check fenestrations
        for (const fen of surface.fenestrations) {
          if (selectedIds.includes(fen.id)) {
            found = true;
            for (const v of fen.vertices) {
              minX = Math.min(minX, v.x);
              minY = Math.min(minY, v.y);
              minZ = Math.min(minZ, v.z);
              maxX = Math.max(maxX, v.x);
              maxY = Math.max(maxY, v.y);
              maxZ = Math.max(maxZ, v.z);
            }
          }
        }
      }
    }

    if (!found) return;

    // Calculate center and size of selection
    const selCenter = {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
      z: (minZ + maxZ) / 2,
    };
    const selSize = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 2);
    const selDistance = selSize * 2.5;

    // Move camera to look at selection (IDF z-up -> Three.js y-up)
    camera.position.set(
      selCenter.x + selDistance * 0.7,
      selCenter.z + selDistance * 0.7,
      selCenter.y + selDistance * 0.7
    );
    camera.lookAt(selCenter.x, selCenter.z, selCenter.y);

    if (controlsRef.current) {
      controlsRef.current.target.set(selCenter.x, selCenter.z, selCenter.y);
      controlsRef.current.update();
    }
  }, [camera, geometry, selectedIds, controlsRef]);

  // Respond to camera preset changes
  useEffect(() => {
    if (cameraPreset) {
      moveToPreset(cameraPreset);
    }
  }, [cameraPreset, moveToPreset]);

  // Respond to fit all trigger
  useEffect(() => {
    if (fitAllTrigger && fitAllTrigger > 0) {
      fitToView();
    }
  }, [fitAllTrigger, fitToView]);

  // Respond to zoom to selection trigger
  useEffect(() => {
    if (zoomToSelectionTrigger && zoomToSelectionTrigger > 0) {
      zoomToSelection();
    }
  }, [zoomToSelectionTrigger, zoomToSelection]);

  return null;
}

/**
 * Scene content component.
 */
function SceneContent({
  geometry,
  selectedIds,
  onSelect,
  viewMode,
  colorScheme,
  showNormals = false,
  showGrid = true,
  showAxes = true,
  controlsRef,
  cameraPreset,
  fitAllTrigger,
  zoomToSelectionTrigger,
}: SceneContentProps) {
  const handleSurfaceSelect = useCallback(
    (id: string, ctrlKey: boolean) => {
      if (ctrlKey) {
        // Multi-select: toggle the selection
        if (selectedIds.includes(id)) {
          onSelect(selectedIds.filter((selectedId) => selectedId !== id));
        } else {
          onSelect([...selectedIds, id]);
        }
      } else {
        // Single select: replace selection
        onSelect([id]);
      }
    },
    [selectedIds, onSelect]
  );

  const handleDoubleClick = useCallback((id: string) => {
    // TODO: Isolate zone or zoom to object
    console.log('Double clicked:', id);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    // Clear selection when clicking background
    onSelect([]);
  }, [onSelect]);

  // Calculate grid size based on geometry
  const gridSize = Math.max(
    geometry.boundingBox.max.x - geometry.boundingBox.min.x,
    geometry.boundingBox.max.y - geometry.boundingBox.min.y,
    100
  );

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[100, 100, 50]} intensity={0.8} />
      <directionalLight position={[-100, -100, -50]} intensity={0.3} />

      {/* Camera controller */}
      <CameraController
        geometry={geometry}
        controlsRef={controlsRef}
        cameraPreset={cameraPreset}
        fitAllTrigger={fitAllTrigger}
        zoomToSelectionTrigger={zoomToSelectionTrigger}
        selectedIds={selectedIds}
      />

      {/* Grid - positioned at floor level (IDF z-min mapped to Three.js Y) */}
      {showGrid && (
        <Grid
          args={[gridSize, gridSize]}
          position={[geometry.center.x, geometry.boundingBox.min.z - 0.01, geometry.center.y]}
          cellSize={1}
          cellThickness={0.5}
          cellColor="#cccccc"
          sectionSize={10}
          sectionThickness={1}
          sectionColor="#999999"
          fadeDistance={gridSize * 2}
          infiniteGrid={false}
        />
      )}

      {/* Axes helper */}
      {showAxes && (
        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
          <GizmoViewport labelColor="white" axisHeadScale={1} />
        </GizmoHelper>
      )}

      {/* Click handler for background - horizontal plane at floor level */}
      <mesh
        position={[geometry.center.x, geometry.boundingBox.min.z - 1, geometry.center.y]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleBackgroundClick}
        visible={false}
      >
        <planeGeometry args={[gridSize * 2, gridSize * 2]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Render zones */}
      {geometry.zones.map((zone) => (
        <group key={zone.id}>
          {/* Render surfaces */}
          {zone.surfaces.map((surface) => (
            <group key={surface.id}>
              <SurfaceMesh
                surface={surface}
                colorScheme={colorScheme}
                viewMode={viewMode}
                isSelected={selectedIds.includes(surface.id)}
                onSelect={handleSurfaceSelect}
                onDoubleClick={handleDoubleClick}
                showNormals={showNormals}
              />

              {/* Render fenestrations on this surface */}
              {surface.fenestrations.map((fenestration) => (
                <FenestrationMesh
                  key={fenestration.id}
                  fenestration={fenestration}
                  viewMode={viewMode}
                  isSelected={selectedIds.includes(fenestration.id)}
                  onSelect={handleSurfaceSelect}
                  onDoubleClick={handleDoubleClick}
                />
              ))}
            </group>
          ))}
        </group>
      ))}

      {/* Render shading surfaces */}
      {geometry.shadingSurfaces.map((shading) => (
        <SurfaceMesh
          key={shading.id}
          surface={{
            id: shading.id,
            name: shading.name,
            type: 'wall' as const,
            construction: '',
            zoneName: shading.zoneName ?? 'Shading',
            boundaryCondition: 'outdoors' as const,
            sunExposure: true,
            windExposure: true,
            vertices: shading.vertices,
            normal: shading.normal,
            fenestrations: [],
            ...(shading.sourceLine !== undefined && { sourceLine: shading.sourceLine }),
          }}
          colorScheme={colorScheme}
          viewMode={viewMode}
          isSelected={selectedIds.includes(shading.id)}
          onSelect={handleSurfaceSelect}
          onDoubleClick={handleDoubleClick}
          showNormals={showNormals}
        />
      ))}
    </>
  );
}

/**
 * Main GeometryViewer component.
 */
export function GeometryViewer({
  geometry,
  selectedIds,
  onSelect,
  viewMode,
  colorScheme,
  showNormals = false,
  showGrid = true,
  showAxes = true,
  cameraPreset,
  fitAllTrigger,
  zoomToSelectionTrigger,
}: GeometryViewerProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controlsRef = useRef<any>(null);

  if (!geometry || geometry.zones.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted/30 text-muted-foreground">
        <div className="text-center py-24 px-6 max-w-lg">
          <svg
            className="h-20 w-20 mx-auto text-muted-foreground/50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h2 className="mt-4 text-lg font-medium text-foreground">No Geometry to Display</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This file doesn't contain geometry objects yet. You can add them in Edit Mode or by editing the IDF directly.
          </p>

          {/* Quick start options */}
          <div className="mt-6 space-y-3">
            <div className="rounded-lg border border-border bg-card p-4 text-left">
              <h3 className="text-sm font-medium text-foreground">Required Objects</h3>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <code className="rounded bg-muted px-1">Zone</code> - Define thermal zones
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <code className="rounded bg-muted px-1">BuildingSurface:Detailed</code> - Walls, floors, ceilings
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                  <code className="rounded bg-muted px-1">FenestrationSurface:Detailed</code> - Windows, doors (optional)
                </li>
              </ul>
            </div>

            <p className="text-xs text-muted-foreground">
              Tip: Click "Edit Geometry" above to use the visual zone creation wizard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate initial camera position based on geometry
  const { boundingBox, center } = geometry;
  const size = Math.max(
    boundingBox.max.x - boundingBox.min.x,
    boundingBox.max.y - boundingBox.min.y,
    boundingBox.max.z - boundingBox.min.z
  );
  const distance = size * 2;

  return (
    <div className="h-full w-full">
      <Canvas
        gl={{ antialias: true }}
        dpr={[1, 2]}
        onPointerMissed={() => { onSelect([]); }}
      >
        {/* IDF uses z-up, Three.js uses y-up, so we swap y and z for camera position */}
        <PerspectiveCamera
          makeDefault
          fov={45}
          near={0.1}
          far={10000}
          position={[
            center.x + distance * 0.7,
            center.z + distance * 0.7,
            center.y + distance * 0.7,
          ]}
        />

        {/* IDF uses z-up, Three.js uses y-up, so we swap y and z for orbit target */}
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.1}
          target={[center.x, center.z, center.y]}
          minDistance={1}
          maxDistance={size * 10}
        />

        <color attach="background" args={['#f0f0f0']} />

        <SceneContent
          geometry={geometry}
          selectedIds={selectedIds}
          onSelect={onSelect}
          viewMode={viewMode}
          colorScheme={colorScheme}
          showNormals={showNormals}
          showGrid={showGrid}
          showAxes={showAxes}
          controlsRef={controlsRef}
          cameraPreset={cameraPreset}
          fitAllTrigger={fitAllTrigger}
          zoomToSelectionTrigger={zoomToSelectionTrigger}
        />
      </Canvas>
    </div>
  );
}

/**
 * Camera preset positions.
 * IDF uses z-up, Three.js uses y-up, so we swap y and z.
 */
export function getCameraPresetPosition(
  preset: CameraPreset,
  center: { x: number; y: number; z: number },
  distance: number
): THREE.Vector3 {
  switch (preset) {
    case 'top':
      return new THREE.Vector3(center.x, center.z + distance, center.y);
    case 'bottom':
      return new THREE.Vector3(center.x, center.z - distance, center.y);
    case 'front':
      return new THREE.Vector3(center.x, center.z, center.y + distance);
    case 'back':
      return new THREE.Vector3(center.x, center.z, center.y - distance);
    case 'right':
      return new THREE.Vector3(center.x + distance, center.z, center.y);
    case 'left':
      return new THREE.Vector3(center.x - distance, center.z, center.y);
    case 'isometric':
    default:
      return new THREE.Vector3(
        center.x + distance * 0.7,
        center.z + distance * 0.7,
        center.y + distance * 0.7
      );
  }
}
